// Augment store adapter factory
// Creates an adapter that bridges augment event handlers to game state

/**
 * Create an augment store adapter
 * 
 * @param {Object} handlers - Object containing state access and mutation handlers
 * @param {Function} handlers.getState - Returns current game state
 * @param {Function} handlers.setState - Updates game state (accepts updater function)
 * @param {Function} handlers.getGoldPending - Returns current pending gold value
 * @param {Function} handlers.setGoldPending - Sets pending gold value
 * @returns {Object} The store adapter with augment-related methods
 */
export function createAugmentStoreAdapter(handlers) {
  const { getState, setState, getGoldPending, setGoldPending } = handlers;

  return {
    /**
     * Get the augment ID for a given unit
     */
    getAugmentId(unitId) {
      if (!unitId) return null;
      const state = getState();
      const a = state.augmentsByUnitId?.[unitId];
      return a ? a.augmentId : null;
    },

    /**
     * Add gold to pending rewards (collected during battle)
     */
    addGold(delta) {
      if (!delta) return;
      setGoldPending(getGoldPending() + delta);
      setState((prev) => ({
        augmentGoldBattle: (prev.augmentGoldBattle || 0) + delta
      }));
    },

    /**
     * Get runtime data for a unit (creating if needed)
     */
    getRuntime(unitId) {
      const state = getState();
      const r = { ...(state.augmentRuntime || {}) };
      const existing = r[unitId] || {};
      if (!state.augmentRuntime?.[unitId]) {
        r[unitId] = existing;
        setState({ augmentRuntime: r });
      }
      return existing;
    },

    /**
     * Queue a piece to be added to bench next round
     */
    queueBenchNextRound(pt) {
      if (!pt) return;
      setState((prev) => {
        const base = (prev && prev.pendingBenchRewards) || {};
        const next = { ...base, [pt]: (base[pt] || 0) + 1 };
        return { pendingBenchRewards: next };
      });
    },

    /**
     * Check if the king has a specific augment
     */
    kingHas(augmentId) {
      const state = getState();
      const k = state.kingUnitId;
      if (!k) return false;
      const a = state.augmentsByUnitId?.[k];
      return !!a && a.augmentId === augmentId;
    },

    /**
     * Iterate over all white units on the board
     */
    forEachWhiteUnit(cb) {
      const state = getState();
      const seen = new Set();
      const map = state.unitIdBySquare || {};
      Object.keys(map).forEach((sq) => {
        try {
          const piece = state.game.get(sq);
          if (piece && piece.color === 'w') {
            const unitId = map[sq];
            if (unitId && !seen.has(unitId)) {
              seen.add(unitId);
              cb(unitId);
            }
          }
        } catch (_) {}
      });
      const augMap = state.augmentsByUnitId || {};
      Object.keys(augMap).forEach((unitId) => {
        if (unitId && !seen.has(unitId)) {
          seen.add(unitId);
          cb(unitId);
        }
      });
    },

    /**
     * Get pawn counts for both sides
     */
    getPawnCounts() {
      try {
        const state = getState();
        const board = state.game?.board?.() || [];
        let white = 0;
        let black = 0;
        for (let r = 0; r < board.length; r++) {
          const row = board[r] || [];
          for (let c = 0; c < row.length; c++) {
            const p = row[c];
            if (p && p.type === 'p') {
              if (p.color === 'w') white++; else black++;
            }
          }
        }
        return { white, black };
      } catch (_) {
        return { white: 0, black: 0 };
      }
    }
  };
}






